import * as React from 'react';
import Easy from 'easy-speech';
import {matchVoiceToLanguage} from './helpers';

type EasySpeechType = typeof Easy;

// Dynamically loads the EasySpeech module to avoid ESM import issues.
export async function getEasySpeech(): Promise<EasySpeechType> {
    const speech = await import('easy-speech');

    return speech.default;
}

const SPEECH_RATE = 1.0;
const SPEECH_PITCH = 1.0;
const SPEECH_VOLUME = 1.0;

// Cancel any ongoing speech immediately
const cancelSpeech = (speech: EasySpeechType): void => {
    const {speechSynthesis} = speech.detect();

    // bail unless there’s active speech
    if (!speechSynthesis?.speaking && !speechSynthesis?.pending) {
        return;
    }

    speech.cancel();
};

export type AudioDescriptionProps = {
    audioDescTrack: HTMLTrackElement | null;
    video: HTMLVideoElement | null;
    langCountryCode: string;
    isEnabled?: boolean;
};

export const useAudioDescription = ({
    audioDescTrack,
    video,
    langCountryCode,
    isEnabled = false
}: AudioDescriptionProps) => {
    const easySpeechRef = React.useRef<EasySpeechType | null>(null);
    const [isInitialized, setIsInitialized] = React.useState<boolean>(false);

    const onCueChange = React.useCallback(async () => {
        const speech = easySpeechRef.current;
        if (!video || !speech) return;
        const activeCues = audioDescTrack?.track.activeCues;

        // activeCues = cues with a starting and end time that the playback time is currently in range of
        if (!activeCues || activeCues.length === 0) {
            return;
        }

        const cue = activeCues[0] as VTTCue;
        const {text} = cue;

        try {
            cancelSpeech(speech);

            // Pause video when cue text is being announced to prevent overlaps with original audio
            if (!video.paused) {
                video.pause();
            }
            const voice = matchVoiceToLanguage(langCountryCode, speech);

            await speech.speak({
                text,
                voice,
                rate: SPEECH_RATE,
                pitch: SPEECH_PITCH,
                volume: SPEECH_VOLUME
            });
        } catch (error) {
            console.error('Speech error:', error);
        } finally {
            // Resume video after cue text was fully announced or error
            if (video.paused) {
                await video.play(); // fail-safe resume
            }
        }
    }, [audioDescTrack, langCountryCode, video]);

    React.useEffect(() => {
        if (!isEnabled || !video || !audioDescTrack || isInitialized) return;

        (async () => {
            if (!easySpeechRef.current) {
                try {
                    const speech = await getEasySpeech();
                    await speech.init({maxTimeout: 5000, interval: 250});
                    easySpeechRef.current = speech;
                    setIsInitialized(true);
                } catch (e) {
                    setIsInitialized(false);
                    console.error('Error initializing EasySpeech:', e);
                }
            }
        })();
    }, [isEnabled, video, audioDescTrack, isInitialized]);

    React.useEffect(() => {
        const EasySpeech = easySpeechRef.current;

        if (!isInitialized || !EasySpeech || !video || !audioDescTrack) {
            return;
        }

        // Cancel AD if turned off
        if (!isEnabled) {
            cancelSpeech(EasySpeech);
            return;
        }

        // Cancel AD if user presses play
        if (!video?.paused) {
            cancelSpeech(EasySpeech);
        }

        audioDescTrack.addEventListener('cuechange', onCueChange);

        return () =>
            audioDescTrack.removeEventListener('cuechange', onCueChange);
    }, [
        audioDescTrack,
        isEnabled,
        onCueChange,
        video,
        video?.paused,
        isInitialized
    ]);
};
